/* Copyright (c) 2025 MOCA Systems, Inc. */
import { inject, Injectable } from '@angular/core';
import { defer, firstValueFrom, from, Observable, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { PlanPrintService } from '../plan/services/plan-print.service';
import { PlanPrintApiService } from './plan-print-api.service';
import { IPlanPrintImage } from 'src/app/app-data/constants/plan-print.model';
import { convertCanvasToBlob } from '@appCanvasCommon/export/ExportHelpers';
import { ICapturedTile } from '@appCanvasCommon/export/ExportCapture';

export enum PrintJobStatus {
	Completed = 'completed',
	Failed = 'failed',
}

export interface PrintJobResult {
	step: PrintJobStatus;
	message?: string;
}

const CONVERT_CONCURRENCY = 5;

/**
 * Orchestrates the process of capturing, converting, and uploading plan print image tiles.
 * Handles progress reporting and error management for the print job workflow.
 */
@Injectable({ providedIn: 'root' })
export class PlanPrintJobOrchestratorService {
	private readonly printService = inject(PlanPrintService);
	private readonly printApiService = inject(PlanPrintApiService);

	/**
	 * Convert canvases (of the tiles) to blobs with limited concurrency.
	 * Frees canvas memory right after each attempt.
	 */
	private convertTilesToBlobsPipe(tiles: ICapturedTile[]): Observable<IPlanPrintImage[]> {
		return from(tiles).pipe(
			mergeMap(
				(tile) =>
					convertCanvasToBlob(tile.canvas).pipe(
						map<Blob, IPlanPrintImage>((blob) => ({
							index: tile.index,
							position: tile.position,
							blob,
						})),
						finalize(() => {
							// try to force the browser to discard the current drawing buffer (release memory/ reduce CPU/GPU)
							try {
								tile.canvas.width = 0;
								tile.canvas.height = 0;
							} catch {
								// no-op
								// defensive: just in case canvas is already disposed or not accessible or anything else
							}
						})
					),
				CONVERT_CONCURRENCY
			),
			toArray()
		);
	}

	/**
	 * Converts captured tiles to blobs and uploads them via the Java REST API.
	 */
	private convertAndUploadPipe(
		capturedTiles$: Observable<ICapturedTile[]>,
		projectId: string,
		jobId: string
	): Observable<PrintJobResult> {
		return capturedTiles$.pipe(
			switchMap((tiles) => this.convertTilesToBlobsPipe(tiles)),
			switchMap((results: IPlanPrintImage[]) =>
				this.printApiService.uploadPlanPrintImages(projectId, jobId, results)
			),
			map(() => ({ step: PrintJobStatus.Completed })),
			catchError((err) =>
				of<PrintJobResult>({
					step: PrintJobStatus.Failed,
					message: String(err),
				})
			)
		);
	}

	/**
	 * Captures, converts, and then uploads the image tiles while emitting progress, fails fast on any error.
	 */
	captureConvertUpload(projectId: string): Observable<PrintJobResult> {
		// - wait for the first emitted value after captureMulti triggered (observable pipeline is subscribed before emission)
		// - mix of async/await and observable to make it easier to read
		const capturedTiles$ =
			// 0. defer the whole thing so that subscription happens before captureMulti()
			defer(async () => {
				// 1. create a promise that resolves with the first value emitted by capturedRawTiles$
				const tilesPromise = firstValueFrom(this.printService.capturedRawTiles$);
				// 2. trigger the capture process which will eventually emit tiles to capturedRawTiles$.
				this.printService.captureMulti();
				// 3. wait for the promise to resolve with the captured tiles
				const tiles = await tilesPromise;

				if (!tiles?.length) {
					throw new Error('No tiles captured');
				}

				return tiles;
			});

		const jobId = this.printApiService.generatePlanPrintJobId();

		// subscribe to tiles first and then trigger capture so that we won't miss the emission
		return this.convertAndUploadPipe(capturedTiles$, projectId, jobId);
	}
}
